import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-xl bg-background-100 h-full cursor-pointer"
      >
        <div className="relative h-56 overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
          <div className="absolute top-3 left-3">
            <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-primary-500 text-background-50">
              {article.categoryName}
            </span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="font-heading font-bold text-lg text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-3">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-4 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>{article.date}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
      >
        <div className="w-32 h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700 mb-2">
            {article.categoryName}
          </span>
          <h3 className="font-heading font-semibold text-sm text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.views.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200/70 last:border-b-0 cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <span className="text-xs text-foreground-500 mt-1 block">{article.date}</span>
        </div>
        {article.isHot && (
          <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary-100 text-primary-600 mt-0.5">
            热门
          </span>
        )}
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-700">
            {article.categoryName}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading font-semibold text-base text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{article.author}</span>
          <div className="flex items-center gap-2">
            <span>{article.date}</span>
            <span className="text-foreground-400">·</span>
            <span>{article.views.toLocaleString()} 阅读</span>
          </div>
        </div>
      </div>
    </Link>
  );
}